import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Crown, Menu, X, Shirt } from "lucide-react";
import { LowCreditsBar } from "./LowCreditsBar";

export const Navigation = ({ user, onNavigate, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const handleNavClick = (path) => {
    setMobileMenuOpen(false);
    onNavigate(path);
  };

  const handleLogout = () => {
    setMobileMenuOpen(false);
    onLogout();
  };
  
  return (
    <>
    <nav className="fixed top-0 left-0 right-0 z-50 glass-card">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => handleNavClick(user ? "/hub" : "/")} className="flex items-center gap-2 cursor-pointer" data-testid="nav-logo">
            <Star className="w-6 h-6 text-[#D4AF37]" />
            <span className="font-serif text-2xl tracking-tight">StarFrame AI</span>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {user ? (
              <div className="flex items-center gap-4">
                <button onClick={() => handleNavClick("/hub")} className="text-sm uppercase tracking-widest text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="nav-hub">Hub</button>
                <button onClick={() => handleNavClick("/create")} className="text-sm uppercase tracking-widest text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="nav-create">Create</button>
                <button onClick={() => handleNavClick("/background")} className="text-sm uppercase tracking-widest text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="nav-background">Background</button>
                <button onClick={() => handleNavClick("/outfit")} className="text-sm uppercase tracking-widest text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="nav-outfit">Outfit</button>
                <button onClick={() => handleNavClick("/gallery")} className="text-sm uppercase tracking-widest text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="nav-gallery">Gallery</button>
                <button onClick={() => handleNavClick("/pricing")} className="text-sm uppercase tracking-widest text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="nav-pricing">Pricing</button>
                <div className="flex items-center gap-2 px-4 py-2 glass-card">
                  <Crown className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-sm font-semibold" data-testid="user-credits">{user.credits} Credits</span>
                </div>
                <button onClick={handleLogout} className="text-sm uppercase tracking-widest text-white/40 hover:text-red-400 transition-colors" data-testid="nav-logout">Logout</button>
              </div>
            ) : (
              <>
                <button onClick={() => handleNavClick("/pricing")} className="text-sm uppercase tracking-widest text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="nav-pricing">Pricing</button>
                <button onClick={() => handleNavClick("/start")} className="btn-primary" data-testid="nav-get-started">Get Started</button>
              </>
            )}
          </div>
          
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2" data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
          </button>
        </div>
      </div>
      
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="md:hidden bg-[#0A0A0A] border-t border-white/10">
            <div className="px-6 py-6 space-y-4">
              {user && (
                <div className="flex items-center gap-2 px-4 py-3 glass-card mb-4">
                  <Crown className="w-5 h-5 text-[#D4AF37]" />
                  <span className="font-semibold" data-testid="mobile-credits">{user.credits} Credits</span>
                </div>
              )}
              {user ? (
                <>
                  <button onClick={() => handleNavClick("/hub")} className="block w-full text-left py-3 text-lg text-white/80 hover:text-[#D4AF37] transition-colors border-b border-white/10" data-testid="mobile-nav-hub">Hub</button>
                  <button onClick={() => handleNavClick("/create")} className="block w-full text-left py-3 text-lg text-white/80 hover:text-[#D4AF37] transition-colors border-b border-white/10" data-testid="mobile-nav-create">Create Portrait</button>
                  <button onClick={() => handleNavClick("/background")} className="block w-full text-left py-3 text-lg text-white/80 hover:text-[#D4AF37] transition-colors border-b border-white/10" data-testid="mobile-nav-background">Background Replace</button>
                  <button onClick={() => handleNavClick("/outfit")} className="block w-full text-left py-3 text-lg text-white/80 hover:text-[#D4AF37] transition-colors border-b border-white/10" data-testid="mobile-nav-outfit">Change Outfit</button>
                  <button onClick={() => handleNavClick("/gallery")} className="block w-full text-left py-3 text-lg text-white/80 hover:text-[#D4AF37] transition-colors border-b border-white/10" data-testid="mobile-nav-gallery">My Gallery</button>
                  <button onClick={() => handleNavClick("/pricing")} className="block w-full text-left py-3 text-lg text-white/80 hover:text-[#D4AF37] transition-colors border-b border-white/10" data-testid="mobile-nav-pricing">Pricing</button>
                  <button onClick={handleLogout} className="block w-full text-left py-3 text-lg text-red-400/80 hover:text-red-400 transition-colors" data-testid="mobile-nav-logout">Logout</button>
                </>
              ) : (
                <>
                  <button onClick={() => handleNavClick("/pricing")} className="block w-full text-left py-3 text-lg text-white/80 hover:text-[#D4AF37] transition-colors border-b border-white/10" data-testid="mobile-nav-pricing">Pricing</button>
                  <button onClick={() => handleNavClick("/start")} className="btn-primary w-full mt-4" data-testid="mobile-nav-get-started">Get Started Free</button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
      {user && (
        <div className="fixed top-20 left-0 right-0 z-40">
          <LowCreditsBar credits={user.credits} onNavigate={onNavigate} />
        </div>
      )}
    </>
  );
};
